import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Country } from '../../../core/models/country';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CountryService } from '../../../core/services/country.service';
import { CountryFormComponent } from './country-form/country-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';

@Component({
  selector: 'em-country',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective,
  ],
  templateUrl: './country.component.html',
  styleUrl: './country.component.scss',
  providers: [ConfirmationService],
})
export class CountryComponent {
  countries!: Country[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  constructor(
    private countryService: CountryService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getAllCountries();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.country.title',
        'layout.configurations.country.name',
        'layout.configurations.country.addCountry.successMessage.detail',
        'layout.configurations.country.editCountry.successMessage.detail',
        'layout.configurations.country.addCountry.successMessage.title',
        'layout.configurations.country.editCountry.successMessage.title',
        'layout.configurations.country.code',
        'layout.configurations.country.actions',
        'layout.configurations.country.addCountry.title',
        'layout.configurations.country.editCountry.title',
        'layout.configurations.country.deleteCountry.title',
        'layout.configurations.country.deleteCountry.detail',
        'layout.configurations.country.deleteCountry.delete',
        'layout.configurations.country.deleteCountry.cancel',
        'layout.configurations.country.deleteCountry.successMessage.title',
        'layout.configurations.country.deleteCountry.successMessage.detail',
        'layout.configurations.country.deleteCountry.errorMessage.title',
        'layout.configurations.country.deleteCountry.errorMessage.detail',
        'layout.configurations.country.deleteCountry.cancelDeletion.title',
        'layout.configurations.country.deleteCountry.cancelDeletion.message',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllCountries() {
    this.countryService.getCountries().subscribe((data) => (this.countries = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(CountryFormComponent, {
      header: this.translations['layout.configurations.country.addCountry.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((path) => {
      if (path) {
        this.getAllCountries();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.country.addCountry.successMessage.title'],
          detail: this.translations['layout.configurations.country.addCountry.successMessage.detail'],
        });
      }
    });
  }

  onEdit(country: Country) {
    this.ref = this.dialogService.open(CountryFormComponent, {
      header: this.translations['layout.configurations.country.editCountry.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: country,
    });

    this.ref.onClose.subscribe((path) => {
      if (path) {
        this.getAllCountries();
        if (path.war) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('layout.configurations.country.editCountry.warningMessage.title'),
            detail: this.translate.instant(
              'layout.configurations.country.editCountry.warningMessage.changeFieldsToUpdate',
            ),
          });
        }else if(path.succes){
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.country.editCountry.successMessage.title'],
          detail: this.translations['layout.configurations.country.editCountry.successMessage.detail'],
        });
        }
      }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.country.deleteCountry.detail'],
      icon: 'pi pi-exclamation-triangle',
      header: this.translations['layout.configurations.country.deleteCountry.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.country.deleteCountry.delete'],
      rejectLabel: this.translations['layout.configurations.country.deleteCountry.cancel'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteCountry(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.country.deleteCountry.cancelDeletion.title'],
          detail: this.translations['layout.configurations.country.deleteCountry.cancelDeletion.message'],
          life: 3000,
        });
      },
    });
  }

  deleteCountry(id: number) {
    this.countryService.deleteCountry(id).subscribe({
      next: () => {
        this.getAllCountries();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.country.deleteCountry.successMessage.title'],
          detail: this.translations['layout.configurations.country.deleteCountry.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting country: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.country.deleteCountry.errorMessage.title'],
          detail: this.translations['layout.configurations.country.deleteCountry.errorMessage.detail'],
        });
      },
    });
  }
}
